/* Bold Home — Loewe / Balenciaga / Bottega Veneta energy.

   Full-bleed cinematic hero with COLOSSAL sans headline + accent-color
   block highlight on the italic word. Oversized ACTS section breaks.
   No chapters — only ACTS. */

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import { tenantHref } from "@/lib/tenant";
import { optimizeImageUrl, optimizedSrcSet } from "@/lib/imageOptim";

const FALLBACK_HERO = "https://images.unsplash.com/photo-1497019579728-aa9854a1a3e2?auto=format&fit=crop&w=2400&q=80";

function startingPrice(s) {
  if (s?.price != null) return s.price;
  const vs = Array.isArray(s?.variations) ? s.variations : [];
  const prices = vs.map((v) => v.price).filter((p) => typeof p === "number");
  return prices.length ? Math.min(...prices) : null;
}

/* AccentBlock — wraps an italic word with a solid accent-color block
   highlight (works across line breaks via box-decoration-break). */
function AccentBlock({ children }) {
  return (
    <em
      style={{
        background: "var(--tenant-accent, #C9A961)",
        color: "#FAF9F6",
        padding: "0 0.18em",
        WebkitBoxDecorationBreak: "clone",
        boxDecorationBreak: "clone",
        fontStyle: "italic",
        fontWeight: 500,
      }}
    >
      {children}
    </em>
  );
}

export default function BoldHome() {
  const { settings } = useSiteSettings();
  const studioName = settings?.studio_name || "Studio";
  const portfolio = settings?.portfolio_images || [];
  const hero = settings?.hero_image_url || settings?.hero_image || portfolio[0] || FALLBACK_HERO;
  const heroPos = `${settings?.hero_focal_x ?? 50}% ${settings?.hero_focal_y ?? 50}%`;
  const tagline = settings?.tagline || settings?.hero_descriptor || "Photographs of where you actually lived.";

  const [services, setServices] = useState([]);
  useEffect(() => {
    api.get("/services").then((r) => setServices(r.data || [])).catch(() => {});
    document.title = `${studioName} — A Studio in Acts`;
  }, [studioName]);

  // Parse the headline to find the accent word (in <em>) — fall back to last word
  const headlineText = settings?.hero_headline || "Photographs of where you actually <em>lived</em>.";
  const headlineParts = headlineText.split(/(<em>[^<]*<\/em>)/g).map((p, i) => {
    const m = p.match(/^<em>([^<]*)<\/em>$/);
    return m ? { type: "em", text: m[1], key: i } : { type: "t", text: p, key: i };
  });

  return (
    <div data-testid="bold-home" className="bg-[color:var(--tenant-bg,#FAF9F6)] text-[color:var(--tenant-fg,#0E0D0C)]" style={{ fontFamily: "var(--tenant-sans)" }}>

      {/* ── ACT 01 · Full-bleed cinematic hero with colossal headline ─── */}
      <section className="relative min-h-screen flex items-end" data-testid="bold-hero">
        <div className="absolute inset-0">
          <img src={optimizeImageUrl(hero, { w: 2400 })}
            srcSet={optimizedSrcSet(hero, [800, 1200, 1600, 2000, 2400]) || undefined}
            sizes="100vw"
            alt="" className="w-full h-full object-cover" style={{ objectPosition: heroPos }} loading="eager" fetchpriority="high" decoding="async" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/35 to-transparent" />
        </div>
        <div className="relative z-10 w-full max-w-[1480px] mx-auto px-6 lg:px-12 pb-16 lg:pb-24">
          <div className="text-[10px] uppercase tracking-[0.45em] text-white/70 mb-5">
            ACT 01 — {studioName.toUpperCase()}
          </div>
          <h1
            className="text-white leading-[0.92] tracking-tight"
            style={{
              fontFamily: "var(--tenant-sans)",
              fontWeight: 800,
              fontSize: "clamp(3rem, 11vw, 11rem)",
              letterSpacing: "-0.02em",
            }}
            data-testid="bold-home-headline"
          >
            {headlineParts.map((p) => (p.type === "em" ? <AccentBlock key={p.key}>{p.text}</AccentBlock> : <span key={p.key}>{p.text}</span>))}
          </h1>
        </div>
      </section>

      {/* Oversized accent-color section divider */}
      <div
        className="w-full"
        style={{ height: 24, background: "var(--tenant-accent, #C9A961)" }}
        aria-hidden
      />

      {/* ── ACT 02 · Recent work — asymmetric oversized grid ─────────── */}
      {/* Inverted panel: paints with --tenant-fg as background and
         --tenant-bg as text so the "cinematic stage" identity stays
         intact on every theme (light themes get a dark-ink stage,
         dark themes keep the obsidian look). */}
      <section
        className="py-20 lg:py-32"
        style={{
          background: "var(--tenant-fg, #0E0D0C)",
          color: "var(--tenant-bg, #FAF9F6)",
        }}
        data-testid="bold-act-02">
        <div className="max-w-[1480px] mx-auto px-6 lg:px-12 mb-14 flex items-end justify-between gap-6 flex-wrap">
          <div>
            <div className="text-[10px] uppercase tracking-[0.45em] text-[color:var(--tenant-accent)] mb-3">
              ACT 02 — Recent work
            </div>
            <h2 className="text-5xl lg:text-7xl font-extrabold leading-[0.95] uppercase tracking-tighter" style={{ fontFamily: "var(--tenant-sans)" }}>
              The Stage
            </h2>
          </div>
          <Link to={tenantHref("/portfolio")} className="text-[10px] uppercase tracking-[0.32em] text-[color:var(--tenant-accent)] hover:opacity-70" data-testid="bold-portfolio-link">
            Open the file →
          </Link>
        </div>
        {/* Asymmetric grid — tiles butt up, no gutters */}
        <div className="grid grid-cols-1 sm:grid-cols-12 gap-0">
          {(portfolio.slice(0, 3).length > 0 ? portfolio.slice(0, 3) : [hero, hero, hero]).map((src, i) => {
            const span = i === 0 ? "sm:col-span-7 aspect-[16/10]" : i === 1 ? "sm:col-span-5 aspect-[3/4]" : "sm:col-span-12 aspect-[16/6]";
            return (
              <figure key={`${src}-${i}`} className={`relative overflow-hidden ${span}`} style={{ background: "rgba(var(--tenant-fg-rgb, 14,13,12), 0.5)" }} data-testid={`bold-act-02-tile-${i}`}>
                <img src={optimizeImageUrl(src, { w: 1600 })}
                  srcSet={optimizedSrcSet(src, [800, 1200, 1600, 2000]) || undefined}
                  sizes="(max-width: 640px) 100vw, 50vw"
                  alt="" loading="lazy" decoding="async" className="w-full h-full object-cover" />
                <figcaption className="absolute left-5 bottom-5 text-[10px] uppercase tracking-[0.32em]" style={{ color: "rgba(var(--tenant-bg-rgb, 250,249,246), 0.85)" }}>
                  Plate {String(i + 1).padStart(2, "0")}
                </figcaption>
              </figure>
            );
          })}
        </div>
      </section>

      {/* ── ACT 03 · Terms — bold service cards ─────────────────────── */}
      {services.length > 0 && (
        <section className="bg-[color:var(--tenant-bg,#FAF9F6)] py-24 lg:py-32" data-testid="bold-act-03">
          <div className="max-w-[1480px] mx-auto px-6 lg:px-12">
            <div className="text-[10px] uppercase tracking-[0.45em] text-[color:var(--tenant-accent)] mb-3">
              ACT 03 — Terms
            </div>
            <h2 className="text-5xl lg:text-7xl font-extrabold leading-[0.95] uppercase tracking-tighter mb-14" style={{ fontFamily: "var(--tenant-sans)" }}>
              On Offer
            </h2>
            <div className="grid sm:grid-cols-3 gap-8">
              {services.slice(0, 3).map((s) => {
                const p = startingPrice(s);
                return (
                  <Link
                    to={tenantHref(`/booking?service=${s.slug}`)}
                    key={s.id || s.slug}
                    className="block group"
                    data-testid={`bold-service-${s.slug}`}
                  >
                    <div
                      className="border-t-4 pt-5"
                      style={{ borderColor: "var(--tenant-accent, #C9A961)" }}
                    >
                      <h3 className="text-2xl lg:text-3xl font-extrabold uppercase leading-tight tracking-tight">
                        {s.name}
                      </h3>
                      <p className="mt-3 text-sm text-[color:var(--tenant-fg,#0E0D0C)]/70 leading-relaxed">
                        {s.short_description || s.tagline || s.description?.slice(0, 100) || ""}
                      </p>
                      <div className="mt-5 text-3xl font-extrabold tabular-nums">
                        {p != null ? `$${p}` : "—"}
                      </div>
                      <div className="mt-5 inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.32em] group-hover:translate-x-1 transition-transform">
                        Reserve →
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* ── ACT 04 · Stage Door — oversize accent block CTA ─────────── */}
      <Link to={tenantHref("/booking")} className="block group" data-testid="bold-stage-door">
        <section
          className="py-20 lg:py-32 text-center"
          style={{ background: "var(--tenant-accent, #C9A961)" }}
        >
          <div className="text-[10px] uppercase tracking-[0.45em] text-white/80 mb-5">ACT 04 — Stage door</div>
          <h2
            className="text-white leading-[0.95] uppercase tracking-tighter font-extrabold italic group-hover:tracking-tight transition-all duration-500"
            style={{
              fontFamily: "var(--tenant-serif)",
              fontStyle: "italic",
              fontSize: "clamp(3rem, 9vw, 8rem)",
            }}
          >
            Book a sitting →
          </h2>
        </section>
      </Link>
    </div>
  );
}
